import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, X as XIcon, Trash2 } from 'lucide-react';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  useDeleteNotification,
  type Notification,
} from '../api/notifications';
import { useSession } from '../api/session';
import { playNotificationDing } from '../lib/sound';

export function NotificationBell(): React.ReactElement {
  const { data: count } = useUnreadCount();
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const qc = useQueryClient();

  // Open a WS to receive notifications + cross-device cache invalidations.
  // The same socket carries {type:'notification'} and {type:'invalidate', scope}
  // messages; we dispatch to TanStack Query directly so other tabs/devices
  // refresh within a second of a mutation anywhere.
  useEffect(() => {
    if (!session?.user) return;
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/ws/notifications`;
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(url);
    } catch { return; }
    ws.onmessage = (ev): void => {
      let msg: { type?: string; scope?: string } | null = null;
      try {
        msg = JSON.parse(String(ev.data ?? ''));
      } catch { return; }
      if (!msg) return;
      if (msg.type === 'notification') {
        playNotificationDing();
        window.dispatchEvent(new Event('sl:notifications:invalidate'));
        qc.invalidateQueries({ queryKey: ['notifications'] });
        return;
      }
      if (msg.type === 'invalidate') {
        switch (msg.scope) {
          case 'hierarchy':
            qc.invalidateQueries({ queryKey: ['groups'] });
            qc.invalidateQueries({ queryKey: ['sets'] });
            qc.invalidateQueries({ queryKey: ['links'] });
            break;
          case 'config':
            qc.invalidateQueries({ queryKey: ['user-config'] });
            break;
          case 'contacts':
            qc.invalidateQueries({ queryKey: ['contacts'] });
            break;
          case 'all':
            qc.invalidateQueries();
            break;
        }
      }
    };
    return () => {
      try { ws?.close(); } catch { /* */ }
    };
  }, [session?.user, qc]);

  // Browser Notification permission prompt once we have a session
  useEffect(() => {
    if (!session?.user) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }
  }, [session?.user]);

  return (
    <>
      <button
        type="button"
        className="sl-bell-btn"
        aria-label="Notifications"
        onClick={() => setOpen(true)}
      >
        <Bell size={14} />
        {count && count.count > 0 ? (
          <span className="sl-bell-badge">{count.count > 99 ? '99+' : count.count}</span>
        ) : null}
      </button>
      {open && <NotificationModal onClose={() => setOpen(false)} />}
    </>
  );
}

function NotificationModal({ onClose }: { onClose: () => void }): React.ReactElement {
  const { data: notifs, refetch } = useNotifications(false);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  const del = useDeleteNotification();
  const navigate = useNavigate();

  useEffect(() => {
    const onInval = (): void => void refetch();
    window.addEventListener('sl:notifications:invalidate', onInval);
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('sl:notifications:invalidate', onInval);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, refetch]);

  return (
    <div className="sl-modal-overlay" onClick={onClose}>
      <div className="sl-notif-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="sl-notif-header">
          <h2>
            <Bell size={18} /> Notifications
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {(notifs?.length ?? 0) > 0 && (
              <button
                className="sl-btn sl-btn-ghost sl-btn-sm"
                type="button"
                onClick={() => markAll.mutate()}
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
            <button
              className="sl-btn sl-btn-ghost sl-btn-sm"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              <XIcon size={14} />
            </button>
          </div>
        </div>
        <div className="sl-notif-body">
          {(!notifs || notifs.length === 0) && (
            <div className="sl-empty" style={{ padding: 24 }}>No notifications</div>
          )}
          {(notifs ?? []).map((n) => (
            <NotifRow
              key={n.uuid}
              n={n}
              onClick={() => {
                markRead.mutate(n.uuid);
                if (n.actionUrl) {
                  onClose();
                  // If the URL is same-origin, use client-side nav so we don't lose the session.
                  const u = n.actionUrl;
                  if (u.startsWith('/')) navigate(u);
                  else window.location.href = u;
                }
              }}
              onDelete={() => del.mutate(n.uuid)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotifRow({
  n,
  onClick,
  onDelete,
}: {
  n: Notification;
  onClick: () => void;
  onDelete: () => void;
}): React.ReactElement {
  return (
    <div className={`sl-notif-item${n.isRead ? '' : ' sl-unread'}`} onClick={onClick}>
      <div className="sl-notif-item-icon">{n.icon || 'ℹ'}</div>
      <div className="sl-notif-item-body">
        <div className="sl-notif-item-title">{n.title}</div>
        <div className="sl-notif-item-msg">{n.message}</div>
        <div className="sl-notif-item-time">{formatTime(n.createdAt)}</div>
      </div>
      <button
        type="button"
        className="sl-btn sl-btn-ghost sl-btn-sm"
        aria-label="Delete"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function formatTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
